import { clamp, type Point, type Position, type Rect, type Size } from "./geometry";

export const MENU_MARGIN = 8;
const ANCHOR_GAP = 4;
/** Submenus overlap their parent by this much so the pointer can cross without gaps. */
const SUBMENU_OVERLAP = 4;
/** Vertical padding of a panel: a submenu's first item lines up with its parent item. */
const PANEL_PADDING = 4;
/** How far a submenu dropped below its item is indented. */
const SUBMENU_BELOW_INDENT = 16;

/** The furthest a panel of `size` can go right and down while keeping `margin` to the viewport. */
function limits(size: Size, viewport: Size, margin: number): Position {
  return { left: viewport.width - size.width - margin, top: viewport.height - size.height - margin };
}

function keepInViewport(position: Position, size: Size, viewport: Size, margin: number): Position {
  const max = limits(size, viewport, margin);
  return { left: clamp(position.left, margin, max.left), top: clamp(position.top, margin, max.top) };
}

function placeBelowElement(rect: Rect, size: Size, viewport: Size, margin: number): Position {
  const below = rect.bottom + ANCHOR_GAP;
  const above = rect.top - ANCHOR_GAP - size.height;
  const fitsBelow = below + size.height <= viewport.height - margin;
  const top = fitsBelow || above < margin ? below : above;
  return keepInViewport({ left: rect.left, top }, size, viewport, margin);
}

function placeAtPoint({ x, y }: Point, size: Size, viewport: Size, margin: number): Position {
  const left = x + size.width > viewport.width - margin ? x - size.width : x;
  const top = y + size.height > viewport.height - margin ? y - size.height : y;
  return keepInViewport({ left, top }, size, viewport, margin);
}

/**
 * Where to put a menu of `size` opened at `point` (pointer) or below `rect` (keyboard, element):
 * flips to the other side when it does not fit and never leaves the viewport.
 */
export function placeMenu(anchor: { point: Point; rect?: Rect | null }, size: Size, viewport: Size, margin = MENU_MARGIN): { left: number; top: number } {
  if (anchor.rect) return placeBelowElement(anchor.rect, size, viewport, margin);
  return placeAtPoint(anchor.point, size, viewport, margin);
}

/**
 * A submenu opens to the right of its parent panel, or to the left when there is no room. On
 * narrow screens (a phone) where neither fits, it drops below its item, slightly indented, over
 * the parent.
 */
export function placeSubmenu(
  item: Rect,
  parent: Rect,
  size: Size,
  viewport: Size,
  margin = MENU_MARGIN,
): { left: number; top: number; side: "right" | "left" | "below" } {
  const max = limits(size, viewport, margin);
  const besideTop = clamp(item.top - PANEL_PADDING, margin, max.top);
  const right = parent.right - SUBMENU_OVERLAP;
  const left = parent.left - size.width + SUBMENU_OVERLAP;
  if (right <= max.left) return { left: right, top: besideTop, side: "right" };
  if (left >= margin) return { left, top: besideTop, side: "left" };
  return {
    left: clamp(item.left + SUBMENU_BELOW_INDENT, margin, max.left),
    top: clamp(item.bottom - PANEL_PADDING, margin, max.top),
    side: "below",
  };
}
